import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { LuTarget, LuEye } from 'react-icons/lu';
import { publicApi } from '../../services/publicApi';

const About = () => {
  const [aboutContent, setAboutContent] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadAbout = async () => {
      try {
        const data = await publicApi.getAboutCompany();
        const selected = data?.[0];
        if (!isMounted) return;
        if (!selected) {
          setAboutContent(null);
          return;
        }

        setAboutContent({
          tagline: selected.tagline || '',
          title: selected.title || '',
          description: selected.description || '',
          mission: selected.mission || '',
          vision: selected.vision || '',
          imageUrl: selected.imageUrl || '',
        });
      } catch {
        if (isMounted) setAboutContent(null);
      }
    };

    loadAbout();
    return () => {
      isMounted = false;
    };
  }, []);

  if (!aboutContent) return null;

  return (
    <section id="about" className="py-24 bg-white dark:bg-black overflow-hidden transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          
          {/* Left Side: Images */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex-1 relative flex justify-center items-center"
          >
            {aboutContent.imageUrl ? (
              <img 
                src={aboutContent.imageUrl} 
                alt="About RaadsoTech" 
                className="w-full h-auto object-contain max-h-[500px] drop-shadow-2xl relative z-10"
              />
            ) : null}
            
            {/* Decorative Element */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-primary/20 rounded-full blur-[80px] -z-0 pointer-events-none"></div>
          </motion.div>

          {/* Right Side: Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex-1"
          >
            <div className="text-primary text-sm font-bold tracking-[0.2em] uppercase mb-4">
              {aboutContent.tagline}
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0B132B] dark:text-white mb-6 leading-tight">
              {aboutContent.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed text-lg">
              {aboutContent.description}
            </p>

            <div className="space-y-6">
              {/* Mission */}
              <div className="flex gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary">
                  <LuTarget size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-black dark:text-white mb-2">Our Mission</h3>
                  <p className="text-gray-500 dark:text-gray-300 leading-relaxed">
                    {aboutContent.mission}
                  </p>
                </div>
              </div>

              {/* Vision */}
              <div className="flex gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary">
                  <LuEye size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-black dark:text-white mb-2">Our Vision</h3>
                  <p className="text-gray-500 dark:text-gray-300 leading-relaxed">
                    {aboutContent.vision}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default About;
